import {
  packFor,
  distribute,
  cycleForOrder,
  scheduledOpening,
  validateSelection,
  pickupAttributes,
  boxAdds,
  prepareCart,
  MAX_COOKIES,
} from "chunkys-order-core";

// v2 of the order widget. Same pricing and checkout core as v1; the difference
// is how a selection gets made. A box button fills the order in one tap, the
// flavour rows are for swapping afterwards, and the sticky bar states which of
// the three steps you are on and why it is blocked when it is.
class ChunkysOrderV2 extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.config = JSON.parse(
      this.querySelector("[data-order-config]").textContent,
    );
    this.strings = this.config.strings;
    this.strings.closed = scheduledOpening(this.config);
    this.strings.reopens = this.strings.closed;
    if (this.config.scheduleEnabled)
      this.strings.nextBatch = this.strings.closed;
    this.cards = [...this.querySelectorAll("[data-flavour]")];
    this.inputs = [...this.querySelectorAll(".slot input")];
    this.limits = this.cards.map((card) => Number(card.dataset.limit));
    this.storageKey = `chunkys-order-v2-${this.config.pickupDate}`;
    const saved = this.readSaved();
    this.tally = this.cards.map((card, i) => {
      const value = saved?.quantities?.[card.dataset.key];
      return Number.isInteger(value) && value >= 0
        ? Math.min(value, this.limits[i])
        : 0;
    });
    if (this.total() > MAX_COOKIES) this.tally.fill(0);
    // Restore a slot the shopper chose before, but never choose one for them.
    // A pickup slot is an appointment, not a preference: picking the first one
    // by default lets someone pay without ever reading the times, and it pushes
    // everyone into the 3:00 slot while the later ones sit empty.
    const restored = this.inputs.find(
      (input) => input.value === saved?.slot && !input.disabled,
    );
    if (restored) {
      restored.checked = true;
      restored.dataset.picked = "true";
    }

    this.abort = new AbortController();
    const options = { signal: this.abort.signal };

    this.addEventListener(
      "click",
      (event) => {
        if (this.busy) return;
        const fill = event.target.closest("[data-fill]");
        if (fill) {
          this.fillBox(Number(fill.dataset.fill));
          return;
        }
        const sheetStep = event.target.closest("[data-sheet-change]");
        if (sheetStep) {
          this.change(this.sheetIndex, Number(sheetStep.dataset.sheetChange));
          return;
        }
        if (event.target.closest("[data-sheet-close]")) {
          this.sheet.close();
          return;
        }
        const step = event.target.closest("[data-change]");
        if (step) {
          this.change(
            this.cards.indexOf(step.closest("[data-flavour]")),
            Number(step.dataset.change),
          );
          return;
        }
        // Anywhere else on a cookie (photo, name, description) opens it.
        const card = event.target.closest("[data-flavour]");
        if (card && !event.target.closest(".counter-v2"))
          this.openSheet(this.cards.indexOf(card));
      },
      options,
    );

    this.sheet = this.querySelector("[data-cookie-sheet]");
    // Pop-up photos load in the background once the page has, so opening a
    // cookie is instant. With Data Saver on, only the one being touched loads.
    this.preloaded = new Map();
    const saveData = navigator.connection?.saveData === true;
    if (!saveData) {
      const warmAll = () => {
        const idle =
          window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
        idle(() => this.cards.forEach((card) => this.preload(card)));
      };
      if (document.readyState === "complete") warmAll();
      else window.addEventListener("load", warmAll, { ...options, once: true });
    }
    this.addEventListener(
      "pointerdown",
      (event) => {
        const card = event.target.closest("[data-flavour]");
        if (card && !event.target.closest(".counter-v2")) this.preload(card);
      },
      options,
    );
    // A tap on the dimmed backdrop (outside the box) closes it.
    this.sheet?.addEventListener(
      "click",
      (event) => {
        if (event.target === this.sheet) this.sheet.close();
      },
      options,
    );

    this.querySelector("[data-start-over]")?.addEventListener(
      "click",
      () => {
        this.tally.fill(0);
        this.save();
        this.render();
      },
      options,
    );

    this.inputs.forEach((input) => {
      // Radios cannot normally be unchecked, so tapping the chosen slot again
      // clears it. Changing your mind back to "not decided yet" should be
      // possible when the thing you picked is a time you have to turn up for.
      input.addEventListener(
        "click",
        () => {
          if (input.dataset.picked !== "true") return;
          input.checked = false;
          input.dataset.picked = "false";
          this.save();
          this.render();
        },
        options,
      );
      input.addEventListener(
        "change",
        () => {
          this.inputs.forEach((slot) => {
            slot.dataset.picked = slot.checked ? "true" : "false";
          });
          this.save();
          this.render();
        },
        options,
      );
    });

    this.querySelectorAll("[data-primary-cta]").forEach((button) =>
      button.addEventListener("click", () => this.advance(), options),
    );

    this.querySelectorAll(".faqs details").forEach((detail) =>
      detail.addEventListener(
        "toggle",
        () => {
          if (detail.open)
            this.querySelectorAll(".faqs details").forEach((other) => {
              if (other !== detail) other.open = false;
            });
        },
        options,
      ),
    );

    window.addEventListener(
      "pageshow",
      () => {
        this.busy = false;
        this.render();
      },
      options,
    );

    this.barObserver = new ResizeObserver(([entry]) => {
      this.style.setProperty(
        "--bar-height",
        `${entry.target.getBoundingClientRect().height}px`,
      );
    });
    this.barObserver.observe(this.querySelector(".sticky-bar-v2"));
    this.wideLayout = window.matchMedia("(min-width: 1151px)");
    this.wideLayout.addEventListener(
      "change",
      () => this.syncActionBar(),
      options,
    );
    this.summaryActionObserver = new IntersectionObserver(
      ([entry]) => {
        this.summaryActionVisible =
          entry.isIntersecting && entry.intersectionRatio >= 0.99;
        this.syncActionBar();
      },
      { threshold: [0, 0.99, 1] },
    );
    this.summaryActionObserver.observe(
      this.querySelector("[data-summary-action]"),
    );
    // The bar waits until the box buttons at the top scroll away (or
    // something is picked), so on first load it doesn't cover them.
    this.pickerObserver = new IntersectionObserver(([entry]) => {
      this.pickerVisible = entry.isIntersecting;
      this.syncActionBar();
    });
    const picker = this.querySelector(".pack-picker");
    if (picker) {
      // Known from the first paint, before the observer's first report.
      const box = picker.getBoundingClientRect();
      this.pickerVisible = box.bottom > 0 && box.top < window.innerHeight;
      this.pickerObserver.observe(picker);
    }
    const bar = this.querySelector(".sticky-bar-v2");
    bar.addEventListener(
      "focusout",
      () => queueMicrotask(() => this.syncActionBar()),
      options,
    );
    this.timer = setInterval(() => {
      const boundary = this.config.batch?.closesAt || this.config.nextOpensAt;
      if (
        this.config.scheduleEnabled &&
        boundary &&
        Date.now() >= boundary * 1000
      )
        location.reload();
      else this.render();
    }, 30000);
    this.render();
    this.initialized = true;
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.barObserver?.disconnect();
    this.summaryActionObserver?.disconnect();
    this.pickerObserver?.disconnect();
    clearInterval(this.timer);
    this.initialized = false;
  }

  change(index, delta) {
    if (index == null || index < 0 || !delta) return;
    if (delta > 0 && this.total() >= MAX_COOKIES) return;
    this.tally[index] = Math.max(
      0,
      Math.min(this.limits[index], this.tally[index] + delta),
    );
    this.save();
    this.render();
  }

  /** Start loading (and decoding) a cookie's pop-up photo, once. */
  preload(card) {
    const src = card?.dataset.largeImage;
    if (!src || this.preloaded.has(src)) return;
    const image = new Image();
    image.decoding = "async";
    image.src = src;
    image.decode?.().catch(() => {});
    this.preloaded.set(src, image);
  }

  /** The cookie pop-up: bigger photo, full description, its own counter. */
  openSheet(index) {
    const card = this.cards[index];
    if (!card || !this.sheet) return;
    this.sheetIndex = index;
    const image = this.sheet.querySelector("[data-sheet-image]");
    // Never show the previous cookie's photo while this one arrives.
    if (image.getAttribute("src") !== card.dataset.largeImage) {
      image.removeAttribute("src");
      image.src = card.dataset.largeImage;
    }
    image.alt = card.dataset.name;
    this.sheet.querySelector("[data-sheet-name]").textContent =
      card.dataset.name;
    const badges = card.querySelector(".badges");
    this.sheet
      .querySelector("[data-sheet-badges]")
      .replaceChildren(...(badges ? [badges.cloneNode(true)] : []));
    this.sheet.querySelector("[data-sheet-description]").textContent =
      card.querySelector(".flavour-v2-description")?.textContent ?? "";
    this.renderSheet();
    this.sheet.showModal();
    // Focus the pop-up itself, not its first button: Safari otherwise draws a
    // focus ring around the × on every tap-open.
    this.sheet.focus({ preventScroll: true });
  }

  renderSheet() {
    if (!this.sheet || this.sheetIndex == null) return;
    const i = this.sheetIndex;
    this.sheet.querySelector("[data-sheet-count]").textContent = this.tally[i];
    this.sheet.querySelector('[data-sheet-change="-1"]').disabled =
      this.busy || this.tally[i] === 0;
    this.sheet.querySelector('[data-sheet-change="1"]').disabled =
      this.busy ||
      this.tally[i] >= this.limits[i] ||
      this.total() >= MAX_COOKIES;
  }

  syncActionBar() {
    const bar = this.querySelector(".sticky-bar-v2");
    // Keep a focused action available until keyboard focus leaves the strip.
    const suppressed = Boolean(
      this.wideLayout?.matches &&
        this.summaryActionVisible &&
        !bar.contains(document.activeElement),
    );
    const parked = Boolean(
      this.pickerVisible &&
        this.total() === 0 &&
        !bar.contains(document.activeElement),
    );
    bar.dataset.suppressed = String(suppressed);
    bar.dataset.parked = String(parked);
    bar.inert = suppressed || parked;
  }

  /**
   * Order flavours are filled in: the fan favourite first, then menu order.
   */
  fillOrder() {
    const usable = this.cards
      .map((card, index) => index)
      .filter((index) => this.limits[index] > this.tally[index]);
    const rank = (index) =>
      this.cards[index].dataset.favourite === "true" ? 0 : 1;
    return usable.sort((a, b) => rank(a) - rank(b) || a - b);
  }

  room(indices) {
    return indices.reduce(
      (sum, index) => sum + (this.limits[index] - this.tally[index]),
      0,
    );
  }

  /**
   * Adds `size` cookies: one of each flavour, then a second of the earliest in
   * fill order, so a six with four usable flavours comes out 2, 2, 1, 1.
   */
  fillBox(size) {
    let added = 0;
    let guard = 0;
    while (added < size && guard++ < MAX_COOKIES) {
      const order = this.fillOrder();
      if (!order.length || this.total() >= MAX_COOKIES) break;
      for (const index of order) {
        if (added >= size || this.total() >= MAX_COOKIES) break;
        if (this.tally[index] >= this.limits[index]) continue;
        this.tally[index] += 1;
        added += 1;
      }
    }
    this.save();
    this.render();
    if (!added) return;
    // Show what landed in the box. Filling otherwise leaves the shopper in the
    // hero with only the bar hinting that anything happened, and a mixed box is
    // exactly the thing people want to see and adjust. Skipped when the list is
    // already on screen, so the page is never yanked for no reason.
    this.reveal(
      this.querySelector("#order"),
      this.querySelector(".flavours-v2"),
    );
  }

  reveal(target, subject) {
    if (!target) return;
    const box = (subject || target).getBoundingClientRect();
    const inView = box.top < window.innerHeight * 0.75 && box.bottom > 0;
    if (inView) return;
    target.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
  }

  step() {
    if (!this.total()) return "cookies";
    if (!this.selectedSlot()) return "time";
    return "pay";
  }

  advance() {
    const step = this.step();
    if (step === "pay") {
      this.checkout();
      return;
    }
    const target =
      step === "time"
        ? this.querySelector(".pickup-picker")
        : this.querySelector(".pack-picker");
    target?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
  }

  total() {
    return this.tally.reduce((a, b) => a + b, 0);
  }
  selectedSlot() {
    return this.inputs.find((input) => input.checked && !input.disabled);
  }
  openSlots() {
    return this.inputs.filter((input) => !input.disabled);
  }
  money(value) {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: this.config.currency || "CAD",
      maximumFractionDigits: value % 100 ? 2 : 0,
    }).format(value / 100);
  }
  readSaved() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey));
    } catch {
      return null;
    }
  }
  save() {
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          quantities: Object.fromEntries(
            this.cards.map((card, i) => [card.dataset.key, this.tally[i]]),
          ),
          slot: this.selectedSlot()?.value,
        }),
      );
    } catch {
      /* Ordering still works when browser storage is unavailable. */
    }
  }
  text(selector, value) {
    this.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  }
  element(tag, className, text) {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  render() {
    const s = this.strings;
    const total = this.total();
    const packed = packFor(total);
    const cycle = cycleForOrder(this.config);
    this.dataset.closed = String(!cycle.open || !this.config.enabled);
    if (this.initialized) this.syncActionBar();
    const opening = this.querySelector("[data-next-opening]");
    if (opening) {
      opening.hidden = !this.config.nextOpensAt || !this.config.enabled;
      this.querySelector("[data-drop-fallback]").hidden = !opening.hidden;
      if (!opening.hidden) {
        const date = new Intl.DateTimeFormat("en-CA", {
          timeZone: "America/Toronto",
          month: "short",
          day: "numeric",
        }).format(new Date(this.config.nextOpensAt * 1000));
        opening.textContent = this.strings.dropOpening.replace("{date}", date);
      }
    }
    const slot = this.selectedSlot();

    this.querySelectorAll("[data-unit-price]").forEach((element) => {
      const size = Number(element.dataset.unitPrice);
      const price = packFor(size).price / size;
      element.textContent = s.perCookie.replace("{amount}", this.money(price));
    });

    // "Six pack" is how you start an order; once there is one, the same button
    // is adding another box, so it says so.
    const boxLabels = { 6: s.boxSix, 4: s.boxFour, 1: s.boxOne };
    const addLabels = { 6: s.addSix, 4: s.addFour, 1: s.addOne };
    this.querySelectorAll(".pack-option[data-fill]").forEach((button) => {
      const name = button.querySelector(".pack-option-name");
      const size = Number(button.dataset.fill);
      if (!name || !boxLabels[size]) return;
      name.textContent = total ? addLabels[size] : boxLabels[size];
    });

    this.cards.forEach((card, i) => {
      card.querySelector("output").textContent = this.tally[i];
      card.classList.toggle("picked", this.tally[i] > 0);
      card.querySelector('[data-change="-1"]').disabled =
        this.busy || this.tally[i] === 0;
      card.querySelector('[data-change="1"]').disabled =
        this.busy || this.tally[i] >= this.limits[i] || total >= MAX_COOKIES;
    });
    this.renderSheet();

    const fragment = document.createDocumentFragment();
    distribute(this.tally).forEach((box, index) => {
      const group = this.element("div", "box");
      const title =
        box.size === 1
          ? s.single
          : `${s.box} ${index + 1} · ${box.size === 6 ? s.six : s.four}`;
      const heading = this.element("div", "box-heading");
      heading.append(
        this.element("strong", "", title),
        this.element("span", "", this.money(box.price)),
      );
      group.append(heading);
      box.quantities.forEach((quantity, i) => {
        if (!quantity) return;
        const line = this.element("div", "box-line");
        line.append(
          this.element("span", "", this.cards[i].dataset.name),
          this.element("span", "", `×${quantity}`),
        );
        group.append(line);
      });
      fragment.append(group);
    });
    if (!total) fragment.append(this.element("p", "empty", s.empty));
    this.querySelector("[data-boxes]").replaceChildren(fragment);

    this.text("[data-total]", this.money(packed.price));
    this.text(
      "[data-savings]",
      total * 500 > packed.price
        ? s.saving.replace("{amount}", this.money(total * 500 - packed.price))
        : s.singleSavings,
    );

    const free =
      total > 0 &&
      total < MAX_COOKIES &&
      this.tally.some((q, i) => q < this.limits[i]) &&
      packFor(total + 1).price <= packed.price;
    const nudge = this.querySelector("[data-free-nudge]");
    if (nudge) nudge.hidden = !free;

    const startOver = this.querySelector("[data-start-over]");
    if (startOver) startOver.hidden = !total;

    this.text(
      "[data-bar-status]",
      total ? `${total} ${s.cookies}${free ? ` · ${s.free}` : ""}` : s.nothing,
    );
    this.text(
      "[data-pickup-heading]",
      cycle.valid
        ? `${s.orderingFor} ${cycle.label} · Leamington`
        : s.nextBatch,
    );

    let countdown = s.nextBatch;
    if (cycle.open) {
      const minutes = Math.max(
        0,
        Math.floor((cycle.cutoff - Date.now()) / 60000),
      );
      countdown = `${s.closes} ${minutes >= 1440 ? `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`}`;
    } else if (cycle.valid) countdown = s.closed;
    this.text("[data-countdown]", countdown);
    // The day is the heading of the picker rather than a note underneath it:
    // by the time anyone is choosing between 3:00 and 4:30, which Friday it is
    // has scrolled well out of view.
    this.text(
      "[data-pickup-day]",
      cycle.valid
        ? s.pickupHeading.replace("{date}", cycle.label)
        : s.nextBatch,
    );
    // The note below now carries only exceptions, so it stays quiet when there
    // is nothing wrong.
    const note = !this.openSlots().length
      ? s.allFull
      : cycle.valid
        ? ""
        : s.nextBatch;
    this.text("[data-cycle-note]", note);
    const noteEl = this.querySelector("[data-cycle-note]");
    if (noteEl) noteEl.hidden = !note;

    // The appointment is restated next to the money, because by the time the
    // total is on screen the picker has scrolled away on a phone.
    const confirm = this.querySelector("[data-pickup-confirm]");
    if (confirm) {
      const show = Boolean(total && slot && cycle.valid);
      confirm.hidden = !show;
      if (show)
        confirm.textContent = s.pickupConfirm
          .replace("{date}", cycle.label)
          .replace("{slot}", slot.dataset.slotLabel);
    }

    const ready = validateSelection(
      this.tally,
      this.limits,
      slot?.value,
      this.openSlots().map((input) => input.value),
    );
    const connected =
      this.cards.every((card) => card.dataset.variantId) &&
      this.inputs.length > 0;
    const canPay =
      ready && cycle.open && this.config.enabled && connected && !this.busy;

    const step = this.step();

    const cta =
      this.busy && step === "pay"
        ? s.busy
        : step === "pay"
          ? `${s.pay} · ${this.money(packed.price)}`
          : step === "time"
            ? s.pickTime
            : s.pickCookies;
    this.querySelectorAll("[data-primary-cta]").forEach((button) => {
      button.textContent = cta;
      button.disabled = step === "pay" && !canPay;
    });

    // v1 hid the blocking reason on phones, so a disabled button had no
    // explanation. The bar carries it here.
    const reason =
      this.busy || canPay || step !== "pay"
        ? ""
        : !cycle.open
          ? cycle.valid
            ? s.reopens
            : s.nextBatch
          : !this.openSlots().length
            ? s.allFull
            : !this.config.enabled || !connected || !cycle.valid
              ? s.unavailable
              : s.failed;
    this.querySelectorAll("[data-bar-reason], [data-summary-reason]").forEach(
      (reasonEl) => {
        reasonEl.textContent = reason;
        reasonEl.hidden = !reason;
      },
    );
    // One thing in the meta row at a time. A blocked checkout needs explaining
    // more than a deadline does, and if the block is that ordering has closed,
    // the countdown would only be repeating it.
    const barCountdown = this.querySelector(".bar-countdown");
    if (barCountdown) barCountdown.hidden = Boolean(reason);

    this.text(
      "[data-announcement]",
      total ? `${total} ${s.cookies}, ${this.money(packed.price)}` : s.nothing,
    );
  }

  async request(path, body) {
    const response = await fetch(`${this.dataset.root}${path}`, {
      method: body ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(20000),
    });
    const data = await response.json();
    if (!response.ok)
      throw new Error(
        typeof data.description === "string"
          ? data.description
          : this.strings.failed,
      );
    return data;
  }

  async checkout() {
    if (this.busy) return;
    const error = this.querySelector("[data-order-error]");
    error.hidden = true;
    this.busy = true;
    this.render();
    try {
      if (this.config.preview) throw new Error(this.strings.preview);
      if (!cycleForOrder(this.config).open)
        throw new Error(this.strings.closed);
      const slot = this.selectedSlot();
      if (
        !validateSelection(
          this.tally,
          this.limits,
          slot?.value,
          this.openSlots().map((input) => input.value),
        )
      )
        throw new Error(this.strings.failed);
      const attributes = pickupAttributes(this.config.batch, {
        start: slot.value,
        label: slot.dataset.slotLabel,
      });
      const items = boxAdds(this.tally).map((add) => {
        const id = Number(this.cards[add.index].dataset.variantId);
        if (!id) throw new Error(this.strings.unavailable);
        return {
          id,
          quantity: add.quantity,
          properties: {
            _chunkys_builder: "v2",
            _chunkys_box: String(add.box),
            _chunkys_pack: String(add.size),
          },
        };
      });
      const verified = await prepareCart(
        this.request.bind(this),
        items,
        attributes,
        this.strings.failed,
        packFor(this.total()).price,
      );
      this.text("[data-total]", this.money(verified.total_price));
      if (!cycleForOrder(this.config).open)
        throw new Error(this.strings.closed);
      window.location.assign(`${this.dataset.root}checkout`);
    } catch (cause) {
      error.textContent = cause.message || this.strings.failed;
      error.hidden = false;
      this.busy = false;
      this.render();
    }
  }
}

if (!customElements.get("chunkys-order-v2"))
  customElements.define("chunkys-order-v2", ChunkysOrderV2);
